import { FlexCodeTransactionStatus, HighnoteTransactionEvent } from 'API';
import { isTransactionFinalized } from 'app/services/agave/erpUtils';
import { AccountType, resolveStatus, StatusKey } from 'util/workflowStatus';

/**
 * Who may edit a transaction's coding, notes, receipts and fuel capture.
 *
 * One rule: you may edit while the transaction is waiting on you — you hold the
 * step it sits on, which is the same thing that decides whether you can move it
 * forward. Ownership is not part of it; a group admin who reviews a group edits
 * that group's transactions, their own included.
 *
 * Frontend only, deliberately. updateTransaction checks who the caller is and
 * never where the transaction sits, so the posted/approved lock this extends has
 * always been advisory too. SEE-1475's audit trail covers accountability, and
 * server-side enforcement is its own ticket — it would have to cover the
 * terminal lock as well.
 *
 * Deliberately NOT a role exemption for administrators: they pass every branch
 * below through the capabilities they already hold, so a step added to the chain
 * later is covered without a second place to remember.
 */
export type EditLockContext = {
  /** The per-company opt-in. Off keeps today's rules. */
  strictMode: boolean;
  accountType: AccountType;
  approvalLevels: number;
  currentUserId: string | null;
  isCardholder: boolean;
  canMarkTransactionReviewed: boolean;
  canApproveTransactionFinal: boolean;
  canPostTransactionToErp: boolean;
  canReviewGroupTransaction: (transaction: HighnoteTransactionEvent) => boolean;
  canApproveGroupStep: (transaction: HighnoteTransactionEvent, status: StatusKey | null) => boolean;
  /** Client-side in-flight post, before erpPostingStatus comes back. */
  isTransactionPosting?: boolean;
};

const GROUP_STEPS: StatusKey[] = ['groupApproval', 'groupApproval1', 'groupFinalApproval'];
const COMPANY_APPROVAL_STEPS: StatusKey[] = ['pendingApproval', 'pendingApproval1', 'pendingFinalApproval'];

/**
 * Today's rules, kept for accounts that have not opted in. The terminal cases
 * are handled by the caller, so only the two non-terminal locks remain: flex
 * closes to cardholders once reviewed, and connected closes while a post is
 * in flight.
 */
const legacyEditable = (transaction: HighnoteTransactionEvent, ctx: EditLockContext): boolean => {
  if (ctx.accountType === 'flex') {
    return !(
      ctx.isCardholder && transaction.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED
    );
  }
  return !ctx.isTransactionPosting;
};

export const canEditTransaction = (
  transaction: HighnoteTransactionEvent | null | undefined,
  ctx: EditLockContext
): boolean => {
  if (!transaction) return false;

  // Posted, bypassed or flex-approved is closed to everyone, administrators
  // included, whether or not the account opted into the rule.
  if (isTransactionFinalized(transaction)) return false;

  if (!ctx.strictMode) return legacyEditable(transaction, ctx);

  const status = resolveStatus({
    transaction,
    accountType: ctx.accountType,
    approvalLevels: ctx.approvalLevels,
    groupApprovalLevels: transaction.groupApprovalLevels ?? 0,
    isTransactionPosting: ctx.isTransactionPosting,
  });

  // No resolvable position means no step to hold. Fall open rather than lock on
  // a status the app itself does not display.
  if (status == null) return true;

  const holdsReview = ctx.canMarkTransactionReviewed && ctx.canReviewGroupTransaction(transaction);
  const isOwner = !!ctx.currentUserId && transaction.userId === ctx.currentUserId;

  if (status === 'new' || status === 'returned') return isOwner || holdsReview;

  if (status === 'needsReview') {
    // A row with no recorded position may predate the Submit step entirely, so
    // its cardholder never had a chance to submit it. Keep it open to them.
    const mayNeverHaveBeenSubmitted = !transaction.workflowStatus;
    return holdsReview || (mayNeverHaveBeenSubmitted && isOwner);
  }

  if (GROUP_STEPS.includes(status)) {
    return ctx.canApproveTransactionFinal || ctx.canApproveGroupStep(transaction, status);
  }

  if (COMPANY_APPROVAL_STEPS.includes(status)) return ctx.canApproveTransactionFinal;

  // Connected sits here waiting to be posted, so whoever can post still holds
  // it. Flex ends here, and is already terminal above.
  if (status === 'approved') return ctx.accountType === 'connected' && ctx.canPostTransactionToErp;

  return false;
};
